import { useAccountContext } from '@/contexts/account-context'
import {
    isCartDrawerOpenAtom,
    searchVisibleAtom,
    signOutAtom,
} from '@/shared/states/app'
import { localCartAtom } from '@/shared/states/cart'
import eventEmitter from '@/utils/event-emitter'
import { useDisclosure } from '@mantine/hooks'
import { useAtom } from 'jotai'
import _ from 'lodash'
import { useRouter } from 'next-router-mock'
import { useEffect, useMemo, useTransition } from 'react'
import { toast } from 'react-toastify'

type NavigationItem = {
    id: string
    label: string
    href: string | null
}

const NAVIGATION_ITEMS_ALLOWED: NavigationItem[] = [
    {
        id: _.uniqueId('item'),
        label: 'Parcourir',
        href: null,
    },
    {
        id: _.uniqueId('item'),
        label: 'Nouveautés',
        href: '/search?ord=created_at',
    },
    {
        id: _.uniqueId('item'),
        label: 'Promotions',
        href: '/search?on_sale=1',
    },
]

export const useViewModel = () => {
    const [mobileMenuOpened, { open, close }] = useDisclosure(false)
    const [_, startTransition] = useTransition()
    const [, setSearchVisible] = useAtom(searchVisibleAtom)
    const [, setCartDrawerOpen] = useAtom(isCartDrawerOpenAtom)

    const { customer, logout } = useAccountContext()

    const [localCart] = useAtom(localCartAtom)
    const router = useRouter()
    const { asPath } = router

    const cartItemsCount = useMemo(() => {
        if (!localCart) return 0
        return localCart.items.length
    }, [localCart])

    const onSearch = () => {
        startTransition(() => {
            setSearchVisible(true)
        })
    }

    const onSignOut = async () =>
        await toast.promise(logout(), {
            pending: 'Déconnexion en cours...',
            success: 'Déconnexion réussie',
            error: 'Une erreur est survenue',
        })

    const onOpenCart = () => {
        startTransition(() => {
            close() // close the mobile menu first
            setCartDrawerOpen(true)
        })
    }

    const onOpenNewProducts = async () => {
        if (typeof window === 'undefined') return
        window.location.href = '/search?sortBy=latest'
    }

    useEffect(() => {
        eventEmitter.on('CLOSE_MOBILE_MENU', close)

        return () => {
            eventEmitter.off('CLOSE_MOBILE_MENU', close)
        }
    }, [close])

    return {
        onSearch,
        currentPath: asPath,
        customer,
        onSignOut,
        onOpenCart,
        cartItemsCount,
        isLoading: !localCart,
        mobileMenuOpened,
        closeMobileMenu: close,
        openMobileMenu: open,
        onOpenNewProducts,
    }
}
